import type { ComponentType, ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import { useConstant } from "@hooks/useConstant";
import type { DataGridDraftRowOptions } from "./draftRows";

/** `data-*` attributes set on a row element, for consumer styling. */
export type DataGridRowAttributes = Partial<Record<`data-${string}`, string>>;

/** Props of `rowProvider`: the row's data, and its cells to wrap. */
export interface DataGridRowProviderProps<TData> {
    row: TData;
    children: ReactNode;
}

/**
 * Full-width rows between data rows (section labels such as "Invalid
 * documents 3"): one cell spanning every column, never selectable, and
 * skipped by arrow keys and clicks. Client totals and `validateTotal` leave
 * them out. Rows keep their data order: the table doesn't sort or group.
 */
export interface DataGridSeparatorRowOptions<TData extends RowData> {
    isSeparator: (row: TData) => boolean;
    /** The label; the grid follows it with the count of rows the separator heads, like a group row. */
    render: (row: TData) => ReactNode;
    /**
     * The count after the label when the rows it heads aren't all in the data
     * (a collapsed band); undefined counts them.
     */
    getCount?: (row: TData) => number | undefined;
}

/**
 * How many rows each separator heads: its own `getCount`, or the rows after
 * it up to the next separator.
 */
export const countSeparatorRows = <TData extends RowData>({
    rows,
    isSeparator,
    getCount,
}: {
    rows: ReadonlyArray<{ id: string; original: TData }>;
    isSeparator: (row: TData) => boolean;
    getCount?: (row: TData) => number | undefined;
}): Map<string, number> => {
    const counts = new Map<string, number>();
    let separatorId: string | null = null;
    for (const row of rows) {
        if (isSeparator(row.original)) {
            const ownCount = getCount?.(row.original);
            separatorId = ownCount === undefined ? row.id : null;
            counts.set(row.id, ownCount ?? 0);
        } else if (separatorId !== null) {
            counts.set(separatorId, (counts.get(separatorId) ?? 0) + 1);
        }
    }
    return counts;
};

/** Stable readers over the latest row options, safe to call while rendering. */
export interface DataGridRowKinds<TData extends RowData> {
    isSeparator: (row: TData) => boolean;
    renderSeparator: (row: TData) => ReactNode;
    /** `separatorRows.getCount`; undefined where the rows it heads decide. */
    getSeparatorCount: (row: TData) => number | undefined;
    getAttributes: (row: TData) => DataGridRowAttributes | undefined;
    /** `getRowActiveKey`; undefined without it, where the row id stands in. */
    getActiveKey: (row: TData) => string | null | undefined;
    /** `expanding.isParentRow`; undefined without it, where the child count decides. */
    isParentRow: (row: TData) => boolean | undefined;
    /** `rowProvider`, read on each access. */
    readonly Provider:
        | ComponentType<DataGridRowProviderProps<TData>>
        | undefined;
    /** `draftRows.isDraft`; false without `draftRows`. */
    isDraft: (row: TData) => boolean;
    /** `draftRows`, read on each access. */
    readonly draft: DataGridDraftRowOptions<TData> | undefined;
}

export const useRowKinds = <TData extends RowData>(optionsRef: {
    readonly current: {
        separatorRows?: DataGridSeparatorRowOptions<TData>;
        getRowAttributes?: (row: TData) => DataGridRowAttributes;
        getRowActiveKey?: (row: TData) => string | null;
        rowProvider?: ComponentType<DataGridRowProviderProps<TData>>;
        expanding?: { isParentRow?: (row: TData) => boolean };
        draftRows?: DataGridDraftRowOptions<TData>;
    };
}): DataGridRowKinds<TData> =>
    useConstant(() => ({
        isSeparator: (row) =>
            optionsRef.current.separatorRows?.isSeparator(row) ?? false,
        renderSeparator: (row) => optionsRef.current.separatorRows?.render(row),
        getSeparatorCount: (row) =>
            optionsRef.current.separatorRows?.getCount?.(row),
        getAttributes: (row) => optionsRef.current.getRowAttributes?.(row),
        getActiveKey: (row) => optionsRef.current.getRowActiveKey?.(row),
        isParentRow: (row) => optionsRef.current.expanding?.isParentRow?.(row),
        get Provider() {
            return optionsRef.current.rowProvider;
        },
        isDraft: (row) => optionsRef.current.draftRows?.isDraft(row) ?? false,
        get draft() {
            return optionsRef.current.draftRows;
        },
    }));
